import { useLazyQuery } from "@apollo/client";
import { MutationFetchPolicy } from "@apollo/client/core/watchQueryOptions";
import {
  Button,
  Flexbox,
  Icons,
  Modal,
  Text,
  Variants
} from "@sede-x/shell-ds-react-framework";
import { loader } from "graphql.macro";
import { useEffect, useState } from "react";
import { ApolloErrorViewer } from "shared/components/ApolloErrorViewer";
import InlineLoadingPanel from "shared/components/InlineLoadingPanel";
import { GqlResponse, TLinkedTicket, TMovementGroup, TTicket } from "ticketing/ticketing.types";
import { LinkedTicket } from "./LinkedTicket";
import "./movements.css";
const LINKED_TICKETS = loader("../../ticketing-graphql/linkedTickets.graphql");
type TMovementTicket = {
  tickets: TLinkedTicket[];
};
type LinkedTicketsResponse = GqlResponse<TMovementTicket, "movement">;
const FETCH_POLICY_NO_CACHE = {
  fetchPolicy: "no-cache" as MutationFetchPolicy
};
const transformLinkedTickets = (tickets: TLinkedTicket[]) => {
  return tickets.map(t => ({ ...t, startDate: new Date(t.startDate) }));
};

const LinkedTicketContainer = ({
  activeMovementGroup,
  disableLinks,
  onUnlinkTickets,
  onShowTicketRevisions,
  onEditTicket,
  refreshTickets,
  showDocument
}: {
  activeMovementGroup?: TMovementGroup | null;
  disableLinks: boolean;
  onUnlinkTickets: (ticket: TLinkedTicket[]) => void;
  onShowTicketRevisions: (ticketId: string) => void;
  onEditTicket: (ticket: TTicket) => void;
  refreshTickets?: number;
  showDocument: (documentId: string) => void;
}) => {
  const [fetchLinkedTicketsOfMovement, { loading, error }] =
    useLazyQuery<LinkedTicketsResponse>(LINKED_TICKETS, {
      ...FETCH_POLICY_NO_CACHE,
      onCompleted: data => setLinkedTickets(transformLinkedTickets(data.movement.tickets))
    });
  const [linkedTickets, setLinkedTickets] = useState<TLinkedTicket[] | null>();
  const [unlinkTicketsShowConfirmation, setUnlinkTicketsShowConfirmation] = useState(false);

  useEffect(() => {
    setLinkedTickets([]);
    const movementId =
      activeMovementGroup?.activeChild?.id ?? activeMovementGroup?.activeMovement.id;
    if (movementId) {
      fetchLinkedTicketsOfMovement({
        variables: {
          movementId
        }
      });
    }
  }, [
    activeMovementGroup?.activeMovement,
    activeMovementGroup?.activeChild,
    refreshTickets,
    fetchLinkedTicketsOfMovement
  ]);

  const onUnlinkTicket = (ticket: TLinkedTicket) => onUnlinkTickets([ticket]);

  const unlinkAllTickets = () => {
    setUnlinkTicketsShowConfirmation(false);
    if (linkedTickets) {
      onUnlinkTickets(linkedTickets);
    }
  };
  const cancelUnlinkAllTickets = () => setUnlinkTicketsShowConfirmation(false);

  const title = `Linked Tickets (Count:${linkedTickets?.length ?? 0})`;
  return (
    <>
      <Flexbox className="container-component-title">
        <Flexbox alignItems="center" flexGrow="1">
          {title}
        </Flexbox>
        <Button
          className="linked-ticket-button"
          title="Unlink all tickets"
          disabled={disableLinks}
          icon={<Icons.CrossCircle style={{ fill: "#ff6358" }} />}
          onClick={() => setUnlinkTicketsShowConfirmation(true)}
          hidden={
            (linkedTickets?.length ?? 0) === 0 || activeMovementGroup?.activeChild != null
          }></Button>
      </Flexbox>
      <div className="movement-batch-container-wrapper">
        {loading && <InlineLoadingPanel />}
        {error && <ApolloErrorViewer error={error} />}
        {!loading && (linkedTickets?.length ?? 0) === 0 && (
          <div>
            <Text size="small" className="availabel-tkt-container-wrapper">
              No data available.
            </Text>
          </div>
        )}
        {linkedTickets
          ?.sort((t1, t2) => t1.id.localeCompare(t2.id))
          ?.map(lt => {
            return (
              <LinkedTicket
                activeMovementGroup={activeMovementGroup}
                ticket={lt}
                key={lt.id}
                disableLinks={disableLinks}
                onShowTicketRevisions={onShowTicketRevisions}
                onUnlinkTicket={onUnlinkTicket}
                onEditTicket={onEditTicket}
                showDocument={showDocument}
              />
            );
          })}
      </div>
      {unlinkTicketsShowConfirmation && (
        <Modal
          title={"Unlink Tickets"}
          open={unlinkTicketsShowConfirmation}
          onClose={cancelUnlinkAllTickets}
          actions={[
            {
              label: "Cancel",
              action: cancelUnlinkAllTickets,
              props: {
                variant: Variants.Outlined,
                icon: <Icons.Cross />
              }
            },
            {
              label: "Unlink",
              action: unlinkAllTickets,
              props: {
                icon: <Icons.Check />
              }
            }
          ]}>
          <Text> Are you sure you want to unlink all tickets linked to this Movement?</Text>
        </Modal>
      )}
    </>
  );
};

export default LinkedTicketContainer;
